import Phaser from 'phaser';

/**
 * **Os cometas, desenhados: rocha escura, impactos quentes, um núcleo que brilha.**
 *
 * ## O que isto é, e o que nunca decide
 *
 * Uma representação. Cada pedra que aparece, cada parte que falta, cada
 * estilhaço e cada brilho vem de um facto que a autoridade já decidiu — a
 * máscara das partes, a exposição do núcleo, a vida dele, e os eventos que a
 * trama trouxe. Este ficheiro não decide vida, recompensa, exposição nem
 * aquisição, e não tem por onde: nada aqui escreve estado de jogo.
 *
 * ## Texturas geradas uma vez
 *
 * Três, feitas por código no primeiro uso e reutilizadas para sempre: um seixo
 * escuro, um brilho quente e um núcleo quase branco. Nenhuma arte final, nenhum
 * PNG temporário, e nenhuma textura por cor — a variação vem de `setTint`, que
 * é o que mantém o agrupamento do Phaser intacto.
 *
 * ## Variação determinística
 *
 * A silhueta de uma parte sai de `(semente visual, índice)` por uma dobra de
 * bits, e nunca de um sorteio: a mesma rocha tem a mesma forma em duas abas, em
 * duas sessões e depois de uma reconstrução. É a mesma disciplina do campo de
 * estrelas, e pela mesma razão.
 *
 * ## Nada é alocado num quadro
 *
 * As pedras vivem numa piscina com teto, indexadas pela identidade
 * AUTORITATIVA do cometa e da parte. Os estilhaços são um conjunto fixo,
 * reciclado. Não há tween por parte, não há emissor por impacto, e nenhum
 * objeto de configuração é construído dentro do laço de desenho.
 *
 * ## O LOD, e o que ele nunca esconde
 *
 * Quando há muita coisa a acontecer, o que desaparece primeiro são **detritos**
 * e resíduos. Um cometa, uma parte presente, um núcleo revelado e um impacto
 * confirmado desenham-se sempre: são informação, e o teto é sobre enfeite.
 *
 * ## Pendente de inspeção
 *
 * A estética deste ficheiro **não está aprovada**. A inspeção visual do
 * projeto acontece no encerramento, e até lá isto é uma proposta que cumpre o
 * contrato — legível sem arte final — e nada mais do que isso.
 */

const TEXTURE = {
  rock: 'open-world-comet-rock',
  spark: 'open-world-comet-spark',
  core: 'open-world-comet-core',
} as const;

/** O tamanho gerado de cada textura, em pixels. Uma vez, e nunca por instância. */
const ROCK_PX = 64;
const SPARK_PX = 32;
const CORE_PX = 48;

/**
 * O teto dos detritos vivos.
 *
 * O primeiro que o LOD corta. Quarenta chega para uma rocha inteira a partir-se
 * sem que o ecrã fique de pedrinhas, e cortar aqui nunca tira informação:
 * um estilhaço é o eco de um facto que já foi desenhado pelo estado.
 */
const DEBRIS_MAX = 40;

/** Quanto tempo um estilhaço vive, em milissegundos. Curto: resíduo some-se. */
const DEBRIS_LIFE_MS = 420;

/** Quanto tempo o clarão de um impacto vive. Mais curto ainda: é um flash. */
const FLASH_LIFE_MS = 180;

/** As cores da rocha. Escuras, e tingidas a partir de uma textura branca. */
const ROCK_TINTS = [0x4a4a52, 0x3d3a3f, 0x55505a, 0x423f46] as const;

/** O halo quente de um impacto, e o brilho do núcleo. */
const IMPACT_TINT = 0xffb066;
const CORE_TINT = 0xfff2d0;
const CORE_HALO_TINT = 0x8fd8ff;

/** A dobra de bits que dá forma a uma parte. A mesma família do campo de estrelas. */
function hash(a: number, b: number): number {
  let value = (a * 0x1f1f_1f1f) ^ (b * 0x8da6_b343);
  value = Math.imul(value ^ (value >>> 16), 0x21f0_aaad);
  value = Math.imul(value ^ (value >>> 15), 0x735a_2d97);
  return (value ^ (value >>> 15)) >>> 0;
}

function unit(a: number, b: number): number {
  return hash(a, b) / 0x1_0000_0000;
}

/** A superfície de desenho, estrutural como as outras artes deste diretório. */
interface RockGraphics {
  fillStyle(color: number, alpha?: number): RockGraphics;
  fillCircle(x: number, y: number, radius: number): RockGraphics;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): RockGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): RockGraphics;
  };
}

/**
 * **O seixo**, branco para ser tingido.
 *
 * Um disco com mordidas: círculos do fundo comidos pela borda, o que dá uma
 * silhueta irregular sem uma malha e sem um ficheiro. É a mesma técnica das
 * estrelas — geometria simples, gerada uma vez.
 */
function makeRockTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.rock)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = ROCK_PX / 2;
  g.fillStyle(0xff_ff_ff, 1);
  g.fillCircle(c, c, c * 0.92);
  /*
   * As mordidas. Determinísticas e não sorteadas: esta textura é UMA, e a
   * variação entre partes vem da rotação e da escala de cada sprite.
   */
  g.fillStyle(0x00_00_00, 0);
  for (let index = 0; index < 6; index += 1) {
    const angle = (index / 6) * Math.PI * 2;
    const radius = c * 0.22;
    g.fillCircle(c + Math.cos(angle) * c * 0.85, c + Math.sin(angle) * c * 0.85, radius);
  }
  g.generateTexture(TEXTURE.rock, ROCK_PX, ROCK_PX);
  g.destroy();
}

/** O clarão: um núcleo curto e um halo. Aditivo, tingido de quente. */
function makeSparkTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.spark)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = SPARK_PX / 2;
  for (let step = 6; step > 0; step -= 1) {
    g.fillStyle(0xff_ff_ff, 0.1);
    g.fillCircle(c, c, (c * step) / 6);
  }
  g.fillStyle(0xff_ff_ff, 1);
  g.fillCircle(c, c, c * 0.22);
  g.generateTexture(TEXTURE.spark, SPARK_PX, SPARK_PX);
  g.destroy();
}

/** O núcleo revelado: quase branco no meio, com um halo que se lê de longe. */
function makeCoreTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.core)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = CORE_PX / 2;
  for (let step = 8; step > 0; step -= 1) {
    g.fillStyle(0xff_ff_ff, 0.08);
    g.fillCircle(c, c, (c * step) / 8);
  }
  g.fillStyle(0xff_ff_ff, 1);
  g.fillCircle(c, c, c * 0.3);
  g.generateTexture(TEXTURE.core, CORE_PX, CORE_PX);
  g.destroy();
}

/** Uma pedra desenhada: o sprite e de que parte ele é. */
interface RockView {
  readonly sprite: Phaser.GameObjects.Image;
}

/** Um estilhaço vivo. Posição, direção e quando nasceu. */
interface Debris {
  readonly sprite: Phaser.GameObjects.Image;
  x: number;
  y: number;
  vx: number;
  vy: number;
  bornAtMs: number;
  /** `-1` quando está livre. É a piscina, e não uma lista que cresce. */
  life: number;
}

/** O que a cena entrega para desenhar um cometa. Só o que a autoridade disse. */
export interface CometDrawState {
  readonly id: string;
  readonly generation: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly parts: number;
  readonly visualSeed: number;
  readonly mask: number;
  readonly coreExposed: boolean;
  readonly coreHealth: number;
}

/** Um evento a desenhar uma vez. */
export interface CometDrawEvent {
  readonly id: string;
  readonly ordinal: number;
  readonly kind: 'impact' | 'fragment' | 'reveal' | 'acquired' | 'full';
  readonly part: number;
  readonly x: number;
  readonly y: number;
}

export class OpenWorldCometArt {
  /** As pedras vivas, por `id da rocha + índice da parte`. Identidade autoritativa. */
  private readonly rocks = new Map<string, RockView>();
  /** Os núcleos, um por cometa revelado. */
  private readonly cores = new Map<string, Phaser.GameObjects.Image>();
  /** A piscina de estilhaços. Fixa, reciclada, com teto. */
  private readonly debris: Debris[] = [];
  /** Os clarões de impacto, na mesma disciplina. */
  private readonly flashes: Debris[] = [];
  /** O último evento desenhado por cometa: um efeito não se desenha duas vezes. */
  private readonly seenEvent = new Map<string, number>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    const target = scene as unknown as TextureScene;
    makeRockTexture(target);
    makeSparkTexture(target);
    makeCoreTexture(target);
  }

  /**
   * Desenha o estado de todas as rochas na vista, e os efeitos desta trama.
   *
   * `comets` é a verdade; `events` é o que aconteceu desde a última. Uma rocha
   * que saiu da vista some-se porque deixou de estar na lista, e não porque
   * alguém a mandou desaparecer.
   */
  update(
    comets: readonly CometDrawState[],
    events: readonly CometDrawEvent[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<string>();

    for (const comet of comets) {
      const centreX = worldUnits(comet.x);
      const centreY = worldUnits(comet.y);
      const radius = worldUnits(comet.radiusMu);
      for (let index = 0; index < comet.parts; index += 1) {
        if ((comet.mask & (1 << index)) === 0) {
          continue;
        }
        /*
         * A chave é a identidade AUTORITATIVA: o cometa, a geração e a parte.
         * Uma geração nova reconstrói as pedras em vez de as herdar, que é o
         * que faz um renascimento não deixar rastro do corpo anterior.
         */
        const key = `${comet.id}:${comet.generation}:${index}`;
        alive.add(key);
        const view = this.rockAt(key);
        const turn =
          ((comet.visualSeed % 360) + index * Math.floor(360 / Math.max(1, comet.parts))) % 360;
        const radians = (turn * Math.PI) / 180;
        const ring = index % 2 === 0 ? radius / 2 : (radius * 3) / 4;
        view.sprite
          .setVisible(true)
          .setPosition(centreX + Math.cos(radians) * ring, centreY + Math.sin(radians) * ring)
          .setDisplaySize(radius * 0.72, radius * 0.72)
          /* A forma de cada pedra: rotação e achatamento próprios, derivados. */
          .setRotation(unit(comet.visualSeed, index) * Math.PI * 2)
          .setTint(ROCK_TINTS[hash(comet.visualSeed, index) % ROCK_TINTS.length] ?? 0x4a4a52);
      }

      /*
       * **O núcleo, e só quando a autoridade diz que está exposto.**
       *
       * A pulsação é lenta e vem do relógio da cena. Ela não conta nada e não
       * decide nada: se a trama seguinte disser que o núcleo não está exposto,
       * ele desaparece no mesmo quadro.
       */
      if (comet.coreExposed) {
        const core = this.coreAt(comet.id);
        const pulse = 0.78 + Math.sin(nowMs / 260) * 0.16;
        core
          .setVisible(true)
          .setPosition(centreX, centreY)
          .setDisplaySize(radius * 0.7 * pulse, radius * 0.7 * pulse)
          .setAlpha(0.55 + (comet.coreHealth / 1000) * 0.4)
          .setTint(comet.coreHealth > 200 ? CORE_TINT : CORE_HALO_TINT);
      } else {
        this.cores.get(comet.id)?.setVisible(false);
      }
    }

    /* As pedras que já não estão na vista, ou que já não existem. */
    for (const [key, view] of this.rocks) {
      if (!alive.has(key)) {
        view.sprite.setVisible(false);
      }
    }

    for (const event of events) {
      const last = this.seenEvent.get(event.id) ?? 0;
      if (event.ordinal <= last) {
        /* Já foi desenhado. Uma trama repetida não repete o efeito. */
        continue;
      }
      this.seenEvent.set(event.id, event.ordinal);
      const x = worldUnits(event.x);
      const y = worldUnits(event.y);
      switch (event.kind) {
        case 'impact':
          this.flash(x, y, nowMs, 0.5);
          break;
        case 'fragment':
          this.flash(x, y, nowMs, 0.9);
          this.burst(x, y, nowMs, event.ordinal);
          break;
        case 'reveal':
          this.flash(x, y, nowMs, 1.4);
          break;
        case 'full':
          /*
           * Um pulso frio, para nao se confundir com a aquisicao. O que diz a
           * razao e o HUD — a carga cheia esta la, em numeros.
           */
          this.flash(x, y, nowMs, 1.2);
          break;
        case 'acquired':
          /*
           * Um pulso curto e inequívoco, e nada que fique. A joia já é de
           * alguém; a carga dele é que o diz, e inventar aqui uma permanência
           * seria inventar um facto.
           */
          this.flash(x, y, nowMs, 2);
          break;
      }
    }

    this.advance(this.debris, nowMs, DEBRIS_LIFE_MS);
    this.advance(this.flashes, nowMs, FLASH_LIFE_MS);
  }

  /** Larga tudo. Para o fim de uma expedição e para o `SHUTDOWN` da cena. */
  destroy(): void {
    for (const view of this.rocks.values()) {
      view.sprite.destroy();
    }
    this.rocks.clear();
    for (const core of this.cores.values()) {
      core.destroy();
    }
    this.cores.clear();
    for (const entry of this.debris) {
      entry.sprite.destroy();
    }
    this.debris.length = 0;
    for (const entry of this.flashes) {
      entry.sprite.destroy();
    }
    this.flashes.length = 0;
    this.seenEvent.clear();
  }

  private rockAt(key: string): RockView {
    const existing = this.rocks.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const sprite = this.scene.add.image(0, 0, TEXTURE.rock).setDepth(this.depth).setVisible(false);
    const view: RockView = { sprite };
    this.rocks.set(key, view);
    return view;
  }

  private coreAt(id: string): Phaser.GameObjects.Image {
    const existing = this.cores.get(id);
    if (existing !== undefined) {
      return existing;
    }
    const sprite = this.scene.add
      .image(0, 0, TEXTURE.core)
      .setDepth(this.depth + 1)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.cores.set(id, sprite);
    return sprite;
  }

  /** Um clarão curto. Reutiliza um da piscina, ou cria um até ao teto. */
  private flash(x: number, y: number, nowMs: number, scale: number): void {
    const entry = this.take(this.flashes, TEXTURE.spark, DEBRIS_MAX);
    if (entry === null) {
      return;
    }
    entry.x = x;
    entry.y = y;
    entry.vx = 0;
    entry.vy = 0;
    entry.bornAtMs = nowMs;
    entry.life = 1;
    entry.sprite
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(18 * scale, 18 * scale)
      .setTint(IMPACT_TINT);
  }

  /**
   * Poucos detritos, direcionais e limitados.
   *
   * Quatro por parte partida, num leque derivado do ordinal do evento — o mesmo
   * evento produz sempre o mesmo leque, e dois clientes vêem a mesma coisa.
   */
  private burst(x: number, y: number, nowMs: number, ordinal: number): void {
    for (let index = 0; index < 4; index += 1) {
      const entry = this.take(this.debris, TEXTURE.rock, DEBRIS_MAX);
      if (entry === null) {
        return;
      }
      const angle = unit(ordinal, index) * Math.PI * 2;
      const speed = 0.05 + unit(ordinal, index + 91) * 0.06;
      entry.x = x;
      entry.y = y;
      entry.vx = Math.cos(angle) * speed;
      entry.vy = Math.sin(angle) * speed;
      entry.bornAtMs = nowMs;
      entry.life = 1;
      entry.sprite
        .setVisible(true)
        .setPosition(x, y)
        .setDisplaySize(5, 5)
        .setTint(ROCK_TINTS[hash(ordinal, index) % ROCK_TINTS.length] ?? 0x4a4a52);
    }
  }

  /**
   * Um lugar livre na piscina, ou um novo até ao teto.
   *
   * `null` quando o teto foi atingido, e é aí que o LOD age: o efeito seguinte
   * é largado. Nunca uma pedra, nunca um núcleo, nunca um impacto que já foi
   * confirmado e desenhado — só o enfeite que passa do teto.
   */
  private take(pool: Debris[], texture: string, ceiling: number): Debris | null {
    for (const entry of pool) {
      if (entry.life <= 0) {
        return entry;
      }
    }
    if (pool.length >= ceiling) {
      return null;
    }
    const sprite = this.scene.add
      .image(0, 0, texture)
      .setDepth(this.depth + 2)
      .setBlendMode(texture === TEXTURE.spark ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL)
      .setVisible(false);
    const entry: Debris = { sprite, x: 0, y: 0, vx: 0, vy: 0, bornAtMs: 0, life: 0 };
    pool.push(entry);
    return entry;
  }

  /** Avança uma piscina. Sem alocação, e o que envelhece volta a estar livre. */
  private advance(pool: Debris[], nowMs: number, lifeMs: number): void {
    for (const entry of pool) {
      if (entry.life <= 0) {
        continue;
      }
      const age = nowMs - entry.bornAtMs;
      if (age >= lifeMs) {
        entry.life = 0;
        entry.sprite.setVisible(false);
        continue;
      }
      entry.x += entry.vx * 16;
      entry.y += entry.vy * 16;
      entry.sprite.setPosition(entry.x, entry.y).setAlpha(1 - age / lifeMs);
    }
  }
}
